import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Stack } from '@mui/material';
import { useState } from 'react';

import { Text, api } from 'common';

import { CodeMirrorAutocompleteProvider } from '../../../../Code/codeMirrorHooks';
import { BlockCodeMirrorField } from '../../ConfigFields/CodeMirror/CodeMirror';
import { BlockDurationField } from '../../ConfigFields/Duration';
import {
  ScriptContextType,
  getScriptContextKeywords,
} from '../../ConfigFields/DynamicField/ScriptContext';
import { BlockTextField } from '../../ConfigFields/TextField';
import { SharedSubFlowV2BlockFormFields } from '../SubFlow/v2/SharedFormFields';
import { SubflowOrSkillV2FlowKinds } from '../constants';

import DurationUnits = api.FlowDefinition.DurationUnits;

enum Section {
  Condition = 'Condition',
  SubFlow = 'SubFlow',
  AdvancedSettings = 'AdvancedSettings',
}

const REPEAT_ADDITIONAL_AUTO_COMPLETE_LABELS = getScriptContextKeywords(ScriptContextType.Repeat);

export function RepeatBlockFormFields() {
  const [expandedSection, setExpandedSection] = useState<Section | null>(Section.Condition);

  return (
    <Stack>
      {/* Condition Accordion */}
      <Accordion
        expanded={expandedSection === Section.Condition}
        onChange={() =>
          setExpandedSection(expandedSection === Section.Condition ? null : Section.Condition)
        }
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="subflow-content">
          <Text variant="heading5" bold>
            Condition
          </Text>
        </AccordionSummary>
        <AccordionDetails>
          <Stack gap={2}>
            <CodeMirrorAutocompleteProvider
              memoizedAdditionalAutoCompleteLabels={REPEAT_ADDITIONAL_AUTO_COMPLETE_LABELS}
            >
              <BlockCodeMirrorField
                label="Until condition"
                fieldName="config.untilConditionScript"
                helperText="Specify a condition to check before each subflow run. Once this condition is true, the repeating will stop."
                minHeight="0px"
              />
            </CodeMirrorAutocompleteProvider>
            <BlockTextField
              label="Maximum iterations"
              fieldName="config.maximumIterations"
              helperText="Maximum number of times to execute the subflow."
              type="number"
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Subflow Accordion */}
      <Accordion
        expanded={expandedSection === Section.SubFlow}
        onChange={() =>
          setExpandedSection(expandedSection === Section.SubFlow ? null : Section.SubFlow)
        }
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="subflow-content">
          <Text variant="heading5" bold>
            Subflow
          </Text>
        </AccordionSummary>
        <AccordionDetails>
          <SharedSubFlowV2BlockFormFields
            fieldNamePrefix="config.subFlow"
            scriptContextType={ScriptContextType.Repeat}
            variant="SubflowPicker"
            allowedFlowKinds={SubflowOrSkillV2FlowKinds}
          />
        </AccordionDetails>
      </Accordion>

      {/* Advanced Settings Accordion */}
      <Accordion
        expanded={expandedSection === Section.AdvancedSettings}
        onChange={() =>
          setExpandedSection(
            expandedSection === Section.AdvancedSettings ? null : Section.AdvancedSettings
          )
        }
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="advanced-settings-content">
          <Text variant="heading5" bold>
            Advanced settings
          </Text>
        </AccordionSummary>
        <AccordionDetails>
          <Stack gap={2}>
            <BlockDurationField
              fieldName="config.iterationStepDuration"
              unitFieldNameSuffix="units"
              allowedUnits={[DurationUnits.Hours, DurationUnits.Minutes, DurationUnits.Seconds]}
              toggleLabel="Time interval"
              valueLabel="Time interval"
              valueHelperText="Duration to wait between processing items"
              variant="BlockValueTemplate"
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
